<script lang="ts">
	import type { SbImage, SbLink } from '../../types';
	import Image from '../../components/image.svelte';
	import Checkmark from '$lib/icons/checkmark.svelte';
	import { scale, slide } from 'svelte/transition';
	import { toSlug } from '$lib/utils/string';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import SbRichtext from '../../components/sbRichtext.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';

	interface Props {
		content: ISbStoryData<{
			user_label: string;
			label: string;
			subline?: string;
			description: ISbRichtext;
			image?: SbImage;
			link_text?: string;
			link: SbLink;
		}>['content'];
	}

	let { content }: Props = $props();

	let hasLabel = $derived(checkoutSession.hasUserLabel(content.user_label));
	let slug = $derived(`addon_${toSlug(content.user_label)}`);
</script>

<label use:editable={content} for={slug} class="cursor-pointer">
	<div
		class={[
			'flex items-start justify-between gap-x-3 rounded-xl border p-3 transition-colors',
			{ 'border-brown-1': hasLabel, 'border-brown-5': !hasLabel }
		]}
	>
		<div class="flex flex-col gap-y-2">
			<div class="flex gap-x-3">
				{#if content.image?.filename}
					<div class="h-full w-16 flex-shrink-0 overflow-hidden rounded-md bg-green-3">
						<Image
							src={content.image.filename}
							alt={content.image.alt}
							title={content.image.title}
							width={180}
						/>
					</div>
				{/if}
				<div class="select-none">
					<p
						class="text-[16px]/[110%] font-semibold tracking-[-.16px] text-brown-1 lg:text-[20px]/[110%] lg:tracking-[-.2px]"
					>
						{content.label}
					</p>
					{#if content.subline && content.subline.length > 0}
						<p class="mt-2 text-[14px]/[160%] text-brown-4 lg:text-[16px]/[150%]">
							{content.subline}
						</p>
					{/if}
				</div>
			</div>
			<div class="text-[14px]/[160%] text-brown-4 empty:hidden">
				{#if content.description && hasLabel}
					<div transition:slide={{ duration: 200 }}>
						<SbRichtext text={content.description} />
					</div>
				{/if}
				{#if content.link_text && content.link_text.length > 0}
					<a
						class="text-[14px]/[160%] underline"
						href={getSbUrl(content.link)}
						target={content.link.target}>{content.link_text}</a
					>
				{/if}
			</div>
		</div>
		<div class="relative h-6 w-6 flex-shrink-0 overflow-hidden">
			<input
				type="checkbox"
				id={slug}
				checked={hasLabel}
				onchange={(e) => {
					if ((e.target as HTMLInputElement | null)?.checked) {
						checkoutSession.addUserLabel(content.user_label);
					} else {
						checkoutSession.removeUserLabel(content.user_label);
					}
				}}
				class="transition-color h-full w-full cursor-pointer appearance-none rounded-md border-2 border-brown-5 duration-300 ease-in-out checked:border-transparent checked:bg-brown-1 active:scale-[0.80] active:border-transparent active:bg-brown-1"
			/>
			{#if hasLabel}
				<div in:scale={{ duration: 300 }} class="absolute inset-0 flex items-center justify-center">
					<Checkmark class="h-3 w-3 text-white" />
				</div>
			{/if}
		</div>
	</div>
</label>
